import type { ProductKeys } from '../../../common/constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import type { QueryParams } from '../../../common/utils/query-params/types';
/* eslint-disable @atlassian/tangerine/import/no-restricted-paths -- QS-9423: composing existing pure functions from controllers/ui into a standalone query generator. These will be moved to search-client as part of QS-9423. */
import {
	getSelectedFilters,
	type SelectedFiltersReturnType,
} from '../../../controllers/filters/selected/get-selected-filters';
import { getSearchConfig } from '../../../ui/search-page-relay/utils/get-search-config';
/* eslint-enable @atlassian/tangerine/import/no-restricted-paths */

export type SearchFiltersConfig = SelectedFiltersReturnType & {
	selectedProducts: ProductKeys[];
};

export type GetSearchFiltersConfigArgs = {
	/** Raw URL query parameters */
	queryParams: QueryParams;
	/** The cloud ID of the current site */
	cloudId: string;
	/** Available product keys for the current search experience */
	availableProducts: ProductKeys[];
	/** Products excluded from the "all" product filter */
	allFilterExcludedProducts: ProductKeys[];
	/** Third-party connector configurations from bootstrap */
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	/** The current user's account ID, used for "assigned to me" / "watched by me" filters */
	currentUserId?: string | null;
	/** JSM incident filter type IDs */
	jsmIncidentFilterIds: string[];
};

export type GetSearchFiltersConfig = (args: GetSearchFiltersConfigArgs) => SearchFiltersConfig;

/**
 * Pure function that resolves search filter configuration from URL query parameters
 * and bootstrap context.
 *
 * Composes two existing pure functions:
 * 1. getSearchConfig - converts URL query params into store-shaped filters
 * 2. getSelectedFilters - converts store-shaped filters into query variable format
 *
 * This function has no React dependencies and can be called from SSR or preloading contexts.
 */
export const getSearchFiltersConfig: GetSearchFiltersConfig = ({
	queryParams,
	cloudId,
	availableProducts,
	allFilterExcludedProducts,
	thirdPartyConfigs,
	currentUserId,
	jsmIncidentFilterIds,
}) => {
	const { filters, selectedProducts, selectedDatasource } = getSearchConfig({
		cloudId,
		queryParams,
		thirdPartyConfigs,
	});

	const selectedFilters = getSelectedFilters({
		filters,
		selectedProducts,
		selectedDatasource,
		availableProducts,
		allFilterExcludedProducts,
		thirdPartyConfigs,
		currentUserId,
		jsmIncidentFilterIds,
	});

	return {
		selectedProducts,
		...selectedFilters,
	};
};
